import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { Package, Plus, AlertTriangle, TrendingDown, ShoppingCart, Search } from 'lucide-react';

function PackingConsumables() {
  const { API } = useContext(AppContext);
  const [consumables, setConsumables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchConsumables();
  }, []);

  const fetchConsumables = async () => {
    // Mock data
    setConsumables([
      { id: '1', code: 'PC-001', name: 'HDPE Bags (50kg)', category: 'Bags', quantity: 5000, unit: 'pcs', minLevel: 2000, maxLevel: 10000, pricePerUnit: 25, status: 'In Stock', supplier: 'Packing Solutions Ltd.', lastRestocked: '2025-01-28' },
      { id: '2', code: 'PC-002', name: 'Stretch Wrap Film', category: 'Films', quantity: 150, unit: 'rolls', minLevel: 100, maxLevel: 500, pricePerUnit: 450, status: 'In Stock', supplier: 'Packaging India', lastRestocked: '2025-01-30' },
      { id: '3', code: 'PC-003', name: 'Strapping Tape', category: 'Tapes', quantity: 80, unit: 'rolls', minLevel: 100, maxLevel: 300, pricePerUnit: 180, status: 'Low Stock', supplier: 'Tape World', lastRestocked: '2025-01-25' },
      { id: '4', code: 'PC-004', name: 'Wooden Pallets', category: 'Pallets', quantity: 200, unit: 'pcs', minLevel: 100, maxLevel: 500, pricePerUnit: 850, status: 'In Stock', supplier: 'Wood Crafts', lastRestocked: '2025-02-01' },
      { id: '5', code: 'PC-005', name: 'Desiccant Packs', category: 'Moisture Control', quantity: 2000, unit: 'pcs', minLevel: 1500, maxLevel: 5000, pricePerUnit: 8, status: 'In Stock', supplier: 'Dry Solutions', lastRestocked: '2025-01-29' },
      { id: '6', code: 'PC-006', name: 'Corner Protectors', category: 'Protection', quantity: 300, unit: 'pcs', minLevel: 500, maxLevel: 2000, pricePerUnit: 35, status: 'Critical', supplier: 'Safe Pack Co.', lastRestocked: '2025-01-20' },
      { id: '7', code: 'PC-007', name: 'Labels & Stickers', category: 'Labels', quantity: 10000, unit: 'pcs', minLevel: 5000, maxLevel: 20000, pricePerUnit: 2, status: 'In Stock', supplier: 'Print Hub', lastRestocked: '2025-01-31' },
      { id: '8', code: 'PC-008', name: 'Box Staples', category: 'Fasteners', quantity: 25000, unit: 'pcs', minLevel: 10000, maxLevel: 50000, pricePerUnit: 0.5, status: 'In Stock', supplier: 'Staple King', lastRestocked: '2025-02-01' },
    ]);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Stock': return 'bg-green-100 text-green-700 border-green-200';
      case 'Low Stock': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Critical': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const filteredConsumables = consumables.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: consumables.length,
    inStock: consumables.filter(c => c.status === 'In Stock').length,
    lowStock: consumables.filter(c => c.status === 'Low Stock').length,
    critical: consumables.filter(c => c.status === 'Critical').length,
  };

  const totalValue = consumables.reduce((sum, c) => sum + (c.quantity * c.pricePerUnit), 0);

  if (loading) {
    return <div className="text-center py-12">Loading consumables data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="packing-consumables-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Packing Consumables</h1>
          <p className="text-gray-500 mt-1">Manage packing materials inventory</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
            <Plus size={18} />
            <span>Add Item</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Items</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Package className="text-gray-400" size={28} />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <p className="text-sm text-green-600 font-medium">In Stock</p>
          <p className="text-2xl font-bold text-green-700">{stats.inStock}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <div className="flex items-center space-x-2">
            <TrendingDown className="text-orange-500" size={18} />
            <p className="text-sm text-orange-600 font-medium">Low Stock</p>
          </div>
          <p className="text-2xl font-bold text-orange-700">{stats.lowStock}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="text-red-500" size={18} />
            <p className="text-sm text-red-600 font-medium">Critical</p>
          </div>
          <p className="text-2xl font-bold text-red-700">{stats.critical}</p>
        </div>
        <div className="bg-gradient-to-br from-[#1a237e]/5 to-[#283593]/10 rounded-xl p-5 border border-[#1a237e]/20">
          <p className="text-sm text-[#1a237e] font-medium">Total Value</p>
          <p className="text-2xl font-bold text-[#1a237e]">₹{(totalValue / 1000).toFixed(1)}K</p>
        </div>
      </div>

      {/* Alerts */}
      {(stats.lowStock > 0 || stats.critical > 0) && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-red-500 mt-0.5" size={20} />
            <div>
              <h4 className="font-semibold text-red-800">Reorder Alert</h4>
              <p className="text-sm text-red-700 mt-1">
                {stats.critical > 0 && `${stats.critical} item(s) at critical level. `}
                {stats.lowStock > 0 && `${stats.lowStock} item(s) running low.`}
                {' '}Please reorder soon.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Search consumables..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
        />
      </div>

      {/* Consumables Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Code</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Item Name</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Quantity</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Min/Max</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Price/Unit</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Total Value</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredConsumables.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 font-mono text-sm text-[#1a237e] font-medium">{item.code}</td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                  <td className="px-4 py-4">
                    <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-900">{item.quantity.toLocaleString()} {item.unit}</td>
                  <td className="px-4 py-4 text-sm text-gray-500">{item.minLevel.toLocaleString()} / {item.maxLevel.toLocaleString()}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">₹{item.pricePerUnit}</td>
                  <td className="px-4 py-4 text-sm font-medium text-green-600">₹{(item.quantity * item.pricePerUnit).toLocaleString()}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <button className="text-sm text-[#1a237e] hover:text-[#e65100] font-medium flex items-center space-x-1">
                      <ShoppingCart size={14} />
                      <span>Reorder</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PackingConsumables;
